/*
  Вкладка «Начисления» карточки счёта (20.10.2026): помесячные начисления по услугам,
  внесённые руками или пришедшие из учётной системы и файла.

  Решение владельца 20.10.2026: заказчик может работать целиком в системе, без выгрузки
  из 1С. Начисление вносится, правится и убирается здесь. Долг строки - «начислено минус
  оплачено», он приходит с сервера: суммы на клиенте не вычитаются (деньги не считаются
  числами с плавающей точкой).

  23.10.2026, решение владельца по макету 6А «сделать все три»: кроме строки по одной -
  «+ Ряд месяцев (одна услуга, один период)», «Вставить из таблицы Excel» и строка «Итого
  начислено / оплачено / долг» по всему счёту (считает сервер). Тот же экран - шаг мастера.

  Метка источника видна у каждой строки. Строку, которой коснулся человек, ночной обмен с 1С
  не перезаписывает (решение владельца 23.10.2026: «главная цифра это ручной ввод, а с 1С
  это сверка») - расхождение с 1С показано у строки и уведомлением над таблицей.
*/

import { useCallback, useState } from 'react'

import { api } from '../../api/client.js'
import { chargeMonth, date, money } from '../../api/format.js'
import { Empty, Loading, Notice } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import usePagedList from '../usePagedList.js'
import ChargeDialog from './ChargeDialog.jsx'
import ChargePasteDialog from './ChargePasteDialog.jsx'
import ChargeSeriesDialog from './ChargeSeriesDialog.jsx'
import ChargeSource from './ChargeSource.jsx'
import ChargeTotals from './ChargeTotals.jsx'
import RemoveDialog from './RemoveDialog.jsx'
import useEntryList from './useEntryList.js'

const DONE = {
  add: 'Начисление внесено.',
  edit: 'Начисление исправлено.',
  series: 'Ряд месяцев внесён.',
  paste: 'Строки из таблицы внесены.',
}

function AddButtons({ onOpen }) {
  return (
    <div className="row row--tight">
      <button className="button" type="button" onClick={() => onOpen({ kind: 'add' })}>
        Добавить начисление
      </button>
      <button className="button button--quiet" type="button" onClick={() => onOpen({ kind: 'series' })}>
        + Ряд месяцев (одна услуга, один период)
      </button>
      <button className="button button--quiet" type="button" onClick={() => onOpen({ kind: 'paste' })}>
        Вставить из таблицы Excel
      </button>
    </div>
  )
}

function ChargeRow({ row, canEdit, onOpen }) {
  return (
    <tr>
      <td>{chargeMonth(row.period)}</td>
      <td data-label="Услуга">{row.service}</td>
      <td className="table__num" data-label="Начислено">
        {money(row.amount)}
      </td>
      <td className="table__num" data-label="Оплачено">
        {money(row.paid_amount)}
      </td>
      <td className="table__num" data-label="Долг">
        {money(row.debt)}
      </td>
      <td data-label="Срок оплаты">{date(row.due_date)}</td>
      <td className="table__block" data-label="Источник">
        <ChargeSource row={row} />
      </td>
      {canEdit ? (
        <td data-label="Действия">
          <div className="row row--tight">
            <button className="button button--quiet" type="button" onClick={() => onOpen({ kind: 'edit', charge: row })}>
              Изменить
            </button>
            <button className="button button--quiet" type="button" onClick={() => onOpen({ kind: 'remove', charge: row })}>
              Убрать
            </button>
          </div>
        </td>
      ) : null}
    </tr>
  )
}

function ChargeDialogs({ accountId, dialog, onSaved, onClose }) {
  const kind = dialog?.kind
  if (kind === 'add' || kind === 'edit') {
    return <ChargeDialog accountId={accountId} charge={dialog.charge} onSaved={onSaved(DONE[kind])} onClose={onClose} />
  }
  if (kind === 'series') {
    return <ChargeSeriesDialog accountId={accountId} onSaved={onSaved(DONE.series)} onClose={onClose} />
  }
  if (kind === 'paste') {
    return <ChargePasteDialog accountId={accountId} onSaved={onSaved(DONE.paste)} onClose={onClose} />
  }
  if (kind !== 'remove') return null
  return (
    <RemoveDialog
      title={`Убрать начисление за ${chargeMonth(dialog.charge.period)}`}
      action="Убрать начисление"
      onRemove={() => api.removeCharge(accountId, dialog.charge.charge_id)}
      onDone={onSaved('Начисление убрано.')}
      onClose={onClose}
    >
      {dialog.charge.service}: начислено {money(dialog.charge.amount)}. Действие необратимо, в
      журнале действий останется запись.
      {dialog.charge.entered_by_hand
        ? ''
        : ' Строка пришла из учётной системы или файла: следующий обмен вернёт её.'}
    </RemoveDialog>
  )
}

export default function ChargesTab({ accountId, canEdit, onDone }) {
  const load = useCallback((window) => api.charges(accountId, window), [accountId])
  // Счёт за пять лет - шестьдесят месяцев по три услуги: страница в несколько экранов
  // закрывала бы то, ради чего её открывают. С 21.10.2026 страницы режет сервер
  // (требование владельца), здесь - общая панель.
  const { rows, error, reload, bar } = usePagedList(load)
  const totals = useEntryList(useCallback(() => api.chargeTotals(accountId), [accountId]))
  // Одно открытое окно: { kind: 'add' | 'edit' | 'series' | 'paste' | 'remove', charge }.
  const [dialog, setDialog] = useState(null)

  const saved = (text) => () => {
    reload()
    totals.reload()
    onDone?.(text)
  }

  return (
    <section className="section card">
      <div className="row row--spread">
        <h2 className="section__title">Начисления</h2>
        {canEdit ? <AddButtons onOpen={setDialog} /> : null}
      </div>

      <Notice error={error} />
      <ChargeTotals totals={totals.rows} error={totals.error} />
      {rows === null && !error ? <Loading what="Загружаем начисления" /> : null}
      {rows !== null && rows.length === 0 ? (
        <Empty title="Начислений пока нет">
          {canEdit
            ? 'Внесите начисление кнопкой выше, рядом месяцев или строками из таблицы, либо загрузите файл выгрузки: после этого появится долг к взысканию.'
            : 'Начисления появятся после внесения или загрузки файла.'}
        </Empty>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>Месяц</th>
              <th>Услуга</th>
              <th className="table__num">Начислено</th>
              <th className="table__num">Оплачено</th>
              <th className="table__num">Долг</th>
              <th>Срок оплаты</th>
              <th>Источник</th>
              {canEdit ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <ChargeRow key={row.charge_id} row={row} canEdit={canEdit} onOpen={setDialog} />
            ))}
          </tbody>
        </table>
      ) : null}
      <PageBar {...bar} what="начислений" />

      <ChargeDialogs accountId={accountId} dialog={dialog} onSaved={saved} onClose={() => setDialog(null)} />
    </section>
  )
}
